import { useTranslation } from 'react-i18next'
import { FullscreenModal } from '@/components/global/FullscreenModal/FullscreenModal'
import { Button } from '@/components/priority/Button/Button'
import { Flex } from '@/components/priority/Flex/Flex'
import { Text } from '@/components/priority/Text/Text'

type ThemeAppMismatchModalProps = {
  /** Whether the modal is visible */
  show: boolean
  /** App type of the order being edited (e.g. "MDL" or "M3") */
  currentApp: string
  /** App type of the theme the user is trying to apply */
  selectedApp: string
  /** Callback to close the modal without applying the theme */
  onClose: () => void
  /** Callback when the user confirms applying the mismatched theme */
  onConfirm: () => void
}

/**
 * Warning shown when a user selects a theme that belongs to a different app
 * than the order they are editing (e.g. an M3 theme on an MDL order). Applying
 * a cross-app theme can corrupt product layouts, so the change is deferred
 * until the user explicitly confirms.
 *
 * Matches the BleedWarningModal structure and styling.
 */
export const ThemeAppMismatchModal = ({
  show,
  currentApp,
  selectedApp,
  onClose,
  onConfirm,
}: ThemeAppMismatchModalProps) => {
  const { t } = useTranslation()

  return (
    <FullscreenModal show={[show, onClose]} hideClose={false}>
      <Flex
        height="300px"
        width="600px"
        column
        center
        gap="32px"
        padding="0 32px"
      >
        <Flex gap="12px" column align="center">
          <Flex gap="12px" align="center">
            <Text size="32px">⚠️</Text>
            <Text weight="500" size="24px" align="center">
              {t('package.theme.appMismatchModal.title')}
            </Text>
          </Flex>
          <Flex gap="12px" column align="center">
            <Text width="500px" align="center">
              {t('package.theme.appMismatchModal.message', {
                current: currentApp,
                selected: selectedApp,
              })}
            </Text>
            <Text width="500px" align="center" weight="500" size="14px">
              {t('package.theme.appMismatchModal.notice')}
            </Text>
          </Flex>
        </Flex>
        <Flex gap="12px">
          <Button
            default
            onClick={onClose}
            aria-label={t(
              'package.theme.appMismatchModal.cancelButtonAriaLabel',
            )}
            data-testid="theme-app-mismatch-modal-cancel"
          >
            {t('package.theme.appMismatchModal.cancelButton')}
          </Button>
          <Button
            primary
            onClick={onConfirm}
            aria-label={t(
              'package.theme.appMismatchModal.confirmButtonAriaLabel',
            )}
            data-testid="theme-app-mismatch-modal-confirm"
          >
            {t('package.theme.appMismatchModal.confirmButton')}
          </Button>
        </Flex>
      </Flex>
    </FullscreenModal>
  )
}
